import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import IcoUnfoldLess from "@floji-internal/mui-icons/UnfoldLess";
import IcoUnfoldMore from "@floji-internal/mui-icons/UnfoldMore";

import { Strings } from "--root/strings/en";

import { IconButton } from "--components/buttons/iconButton";

import { AVDevices } from "--client/common/TypedStore";
import { useTypedStore } from "--client/common/apis/hooks/useTypedStore";

import { DeviceSelect } from "./DeviceSelect";
import { Label } from "--floponents-web/atoms/Label/Label";

const StyledCameraSettings = styled.div`
    position: relative;
`;

const CameraPreviewVideoContainer = styled.div<{ targetHeight: number }>`
    align-items: center;
    background: ${props => props.theme.palette.black};
    border-radius: ${props => props.theme.radius.large};
    border: 1px solid ${props => props.theme.palette.ui.border};
    box-sizing: border-box;
    display: flex;
    height: ${props => props.targetHeight}px;
    justify-content: center;
    overflow: hidden;
    transition: height ${props => props.theme.transitions.standard} ease;
`;
const CameraPreviewVideo = styled.video`
    align-self: center;
    object-fit: cover;
    width: 100%;
`;

// CB: Was causing issues with syntax highlighter without the return statement
const CameraPreviewExpando = styled(IconButton).attrs(props => {
    return ({
        color: props.theme.palette.createColorSet("white"),
        variant: "translucent",
        size: "small"
    })
})`
    top: ${props => props.theme.spacing(1)};
    left: ${props => props.theme.spacing(1)};
    position: absolute;
`;

const CameraDeviceSelect = styled(DeviceSelect).attrs({ variant: "translucent" })`
    position: absolute;
    top: ${props => props.theme.spacing(1)};
    right: ${props => props.theme.spacing(1)};
`;

export interface CameraSettingsProps {
    mediaStream?: MediaStream;
}
export const CameraSettings = (props: CameraSettingsProps) => {
    const storedDevices = useTypedStore(AVDevices);

    const [currentDeviceId, setCurrentDeviceId] = useState<string>();
    const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>();
    useEffect(() => {
        setCurrentDeviceId(
            props.mediaStream?.getVideoTracks()[0]?.getSettings().deviceId
        );
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                setVideoInputDevices(
                    devices?.filter(device => device.kind === "videoinput")
                );
            })
    }, [props.mediaStream]);

    const cameraVideoContainerRef = useRef<HTMLDivElement>(null);
    const cameraVideoRef = useRef<HTMLVideoElement>(null);

    const [isExpanded, setIsExpanded] = useState(false);
    const handleExpandoClick = useCallback(() => {
        setIsExpanded(v => !v);
    }, []);

    // webcam preview
    useEffect(() => {
        const currentCameraVideo = cameraVideoRef.current;
        if (props.mediaStream && currentCameraVideo) {
            currentCameraVideo.srcObject = props.mediaStream;
            currentCameraVideo.play()
        }
        return () => { currentCameraVideo?.pause(); };
    }, [props.mediaStream]);

    const ExpandoIcon = isExpanded ? IcoUnfoldLess : IcoUnfoldMore;

    const [targetHeight, setTargetHeight] = React.useState<number>(0);

    const resizeObserver = useMemo(() => (
        new ResizeObserver(([entry]) => {
            if (!entry) return;
            setTargetHeight(entry.contentRect.height);
        })
    ), []);

    React.useEffect(() => {
        const currentCameraVideoRef = cameraVideoRef.current;
        if (currentCameraVideoRef) {
            setTargetHeight(currentCameraVideoRef.offsetHeight);
            resizeObserver.observe(currentCameraVideoRef)
        } else {
            setTargetHeight(0);
        }

        return () => {
            if (currentCameraVideoRef) {
                resizeObserver.unobserve(currentCameraVideoRef);
            }
        };
    }, [isExpanded, resizeObserver]);

    return <div>
        <Label>Camera</Label>
        <StyledCameraSettings>
            <CameraPreviewVideoContainer
                ref={cameraVideoContainerRef}
                targetHeight={isExpanded ? targetHeight : 100}>
                <CameraPreviewVideo ref={cameraVideoRef} />
            </CameraPreviewVideoContainer>
            <CameraPreviewExpando icon={<ExpandoIcon />} onClick={handleExpandoClick} />
            <CameraDeviceSelect
                placeholder={Strings.audioVideoSettings.webcamPlaceholder}
                devices={videoInputDevices}
                deviceId={currentDeviceId ?? "default"}
                title={Strings.audioVideoSettings.webcam}
                onChange={deviceId => AVDevices.set({ ...storedDevices, webcamId: deviceId })}
            />
        </StyledCameraSettings>
    </div>;
};
